import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { Button, Typography } from '@material-ui/core';
import EmptyMessage from 'components/@common/EmptyResultSetMessage';
import { ModalContainer, ModalContent, ModalFooter, ModalHeader } from 'components/@common/Modal';
import GET_STUDENT_WITH_LICENSE_USAGE from 'graph/student/get-student-with-license-usage-query';
import useParseActionRouteParams from 'hooks/route-action/use-parse-action-route-params';
import useStore from 'store';
import { StudentLicenseUsage } from 'types/schema-generated-types';
import { getFullName } from 'utilities/name-utils';
import { getActiveStudentProducts } from 'utilities/product-utils';
import SettingsContainer from './SettingsContainer';

const StudentSettingsModal = () => {
  const { t } = useTranslation('SettingsModal');
  const [, { closeModal }] = useStore();
  const { classroomId, studentId } = useParseActionRouteParams();
  const { data, loading } = useQuery(GET_STUDENT_WITH_LICENSE_USAGE, {
    variables: { where: { id: Number(studentId) } }
  });

  const studentLicenseUsage: StudentLicenseUsage[] = data?.student?.licenseUsage ?? [];
  const activeProductsWithCurriculum = useMemo(
    () => getActiveStudentProducts(studentLicenseUsage),
    [studentLicenseUsage]
  );

  if (loading) return null;

  return (
    <ModalContainer
      Header={
        <ModalHeader>
          <Typography variant="h2" component="h1">
            {t('Manage Student Settings')} - <b>{getFullName(data.student)}</b>
          </Typography>
        </ModalHeader>
      }
      Footer={
        <ModalFooter
          Actions={
            <Button
              aria-label={t('Close Settings Modal')}
              variant="contained"
              color="secondary"
              disableElevation
              onClick={closeModal}
            >
              {t('Close')}
            </Button>
          }
        />
      }
    >
      <ModalContent>
        {activeProductsWithCurriculum.length > 0 ? (
          <SettingsContainer
            student={data.student}
            productsWithCurriculum={activeProductsWithCurriculum}
            classroomId={classroomId}
          />
        ) : (
          <EmptyMessage
            style={{ marginTop: '12px' }}
            message={t('No settings available.')}
            subtitle={t(
              `The student either has no license assigned or is licensed for products with no settings.`
            )}
          />
        )}
      </ModalContent>
    </ModalContainer>
  );
};

export default StudentSettingsModal;
